import React from 'react';
import intl from 'react-intl-universal';
import {observer} from 'mobx-react-lite';

import type {AppState} from '@stores/AppState';
import type {Issue} from '../../../../stores/reliability/types';
import {ContextMenu} from '../../../components/ContextMenu/ContextMenu';
import {ArchiveIcon} from '../../../components/icons/ArchiveIcon';
import {DeleteIcon} from '../../../components/icons/DeleteIcon';
import {UnarchiveIcon} from '../../../components/icons/UnarchiveIcon';
import {getMuiIcon} from '../../../components/icons/createMuiIcon';
import {ContextMenuInterface} from '../ContextMenuInterface';

const ReopenIcon = getMuiIcon('restart_alt');

export interface IssueRowOptionsProps extends ContextMenuInterface {
    issue: Issue;
    onDeleteRequest: (issue: Issue) => void;
    onArchiveRequest?: (issue: Issue) => void;
    onReopenRequest?: (issue: Issue) => void;
}

export const openIssueRowOptions = (
    appState: AppState,
    issue: Issue,
    mousePositionParams: any,
    onDeleteRequest: (issue: Issue) => void,
    onArchiveRequest?: (issue: Issue) => void,
    onReopenRequest?: (issue: Issue) => void,
) => {
    appState.setContextMenu({
        ...mousePositionParams,
        id: `issue-row-options-${issue.id}`,
        type: 'issue-row-options',
        withOverlay: true,
        minWidth: 180,
        params: {issue, onDeleteRequest, onArchiveRequest, onReopenRequest},
    });
};

export const IssueRowOptions = observer(({handleClose, isDark, issue, onDeleteRequest, onArchiveRequest, onReopenRequest}: IssueRowOptionsProps) => {
    const isClosed = issue.status !== 'open';
    const isArchivable = onArchiveRequest && isClosed;
    const isReopenable = onReopenRequest && isClosed;
    return (
        <ContextMenu
            isDark={isDark}
            handleClose={handleClose}
            items={[
                ...(isReopenable ? [{
                    text: intl.get('issues.inbox.reopen').d('Reopen issue'),
                    Icon: ReopenIcon,
                    onClick: () => {
                        onReopenRequest!(issue);
                        handleClose();
                        return false;
                    },
                }] : []),
                ...(isArchivable ? [{
                    text: issue.archivedAt
                        ? intl.get('issues.inbox.unarchive').d('Unarchive')
                        : intl.get('issues.inbox.archive').d('Archive'),
                    Icon: issue.archivedAt ? UnarchiveIcon : ArchiveIcon,
                    onClick: () => {
                        onArchiveRequest!(issue);
                        handleClose();
                        return false;
                    },
                }] : []),
                {
                    text: intl.get('common.delete').d('Delete'),
                    Icon: DeleteIcon,
                    onClick: () => {
                        onDeleteRequest(issue);
                        handleClose();
                        return false;
                    },
                },
            ]}
        />
    );
});
